import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Tabs,
    Table,
    DatePicker,
    Input,
    Select,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Menu,
    Typography,
    message,
    Pagination,
    Modal,
    InputNumber,
    Tooltip,
    Checkbox
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    DeleteOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EyeOutlined,
    EditOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    CheckOutlined,
    PlayCircleOutlined,
    CloseCircleOutlined,
    MoreOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ScissorOutlined,
    StopOutlined,
    PlusOutlined,
    CaretUpOutlined,
    CaretDownOutlined,
    FilterOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, isDateField, applyAdvancedFilter, parseValue, redirectToPivotWithData, exportToExcel, printTable, parseDate } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import Chatbot from '../giris/Chatbot.jsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import FormikForm from '../form/FormikForm';
import '../fatura/FaturaPage.css';
import { useNavigate } from 'react-router-dom';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { Option } = Select;

const renderCellContent = (key, text) => {
    const formattedValue = formatCellValue(key, text);

    if (typeof formattedValue === 'string' && (formattedValue.includes('<span') || formattedValue.includes('<div') || formattedValue.includes('<p'))) {
        return <span dangerouslySetInnerHTML={{ __html: formattedValue }} />;
    }

    return <span>{formattedValue}</span>;
};

const isExpired = (record) => {
    const bitisTarihi = record['Geçr. Tarihi'];
    if (!bitisTarihi || bitisTarihi === '0001-01-01' || bitisTarihi === '-') {
        return false;
    }

    const today = new Date();
    const expiryDate = new Date(bitisTarihi);

    if (isNaN(expiryDate.getTime())) {
        return false;
    }

    return expiryDate < today;
};

const iconMap = {
    'EyeOutlined': EyeOutlined,
    'EditOutlined': EditOutlined,
    'FileTextOutlined': FileTextOutlined,
    'DownloadOutlined': DownloadOutlined,
    'CalculatorOutlined': CalculatorOutlined,
    'CheckOutlined': CheckOutlined,
    'PlayCircleOutlined': PlayCircleOutlined,
    'CloseCircleOutlined': CloseCircleOutlined,
    'InfoCircleOutlined': InfoCircleOutlined,
    'ExportOutlined': ExportOutlined,
    'ScissorOutlined': ScissorOutlined,
    'StopOutlined': StopOutlined,
    'MoreOutlined': MoreOutlined,
    'DeleteOutlined': DeleteOutlined,
    'PlusOutlined': PlusOutlined,
};

const ActionButtons = ({ record, actionButtons, darkMode }) => {
    if (!actionButtons || actionButtons.length === 0) {
        return null;
    }

    if (actionButtons.length > 2) {
        const menuItems = actionButtons.map(button => {
            const IconComponent = iconMap[button.icon];
            return {
                key: button.key,
                label: button.label,
                icon: IconComponent ? <IconComponent /> : null,
                onClick: () => button.onClick(record)
            };
        });

        return (
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
            >
                <Button
                    type="text"
                    icon={<MoreOutlined />}
                    size="small"
                    className={darkMode ? 'dark-button' : ''}
                    style={{
                        color: darkMode ? 'var(--dark-text)' : undefined
                    }}
                />
            </Dropdown>
        );
    }

    return (
        <Space size={2} direction="vertical" style={{ width: '100%' }}>
            {actionButtons.map(button => {
                const IconComponent = iconMap[button.icon];
                return (
                    <Button
                        key={button.key}
                        type={button.type}
                        size="small"
                        icon={IconComponent ? <IconComponent /> : null}
                        onClick={() => button.onClick(record)}
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                            color: darkMode ? 'var(--dark-text)' : undefined,
                            width: '100%',
                            fontSize: '12px'
                        }}
                    >
                        {button.label}
                    </Button>
                );
            })}
        </Space>
    );
};

const SirketlerPage = () => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const [activeTab, setActiveTab] = useState('sirketler');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        page: 1,
        pageSize: 50,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [modalButtonType, setModalButtonType] = useState('main-table');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companyFilters, setCompanyFilters] = useState({});
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const sirketlerTabs = [
        {
            key: 'sirketler',
            label: 'Şirketler Liste',
            sqlKey: 'sirketler',
            formManagerId: 95,
            noDateFilter: true,
            actionButtons: {
                'KEY_FIRMA': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            //sadece bazı kullanıcılar düzenleyebiliyor örneğin 26. pozisyona sahip biri ...
                            const id = record.key_firma;
                            if (id) {
                                handleOpenForm(101, 'Şirket Düzenle', 'sirket', 'id', id);
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    }
                ]
            }
        },
        {
            key: 'sirket_dosyalar',
            label: 'Şirket Dosyaları',
            sqlKey: 'sirket_dosyalar',
            formManagerId: 136,
            actionButtons: {
                'KEY_FIRMA_DOSYA_VIEW': [
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.key_firma_dosya;
                            if (id) {
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    }
                ],
                'KEY_FIRMA_DOSYA': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const id = record.key_firma_dosya;
                            if (id) {
                                handleOpenForm(23, 'Şirket Dosya Yükle', 'firma_dosya', 'id', id);
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    }
                ]
            }
        },
        {
            key: 'vekaletler',
            label: 'Vekaletler',
            sqlKey: 'vekaletler',
            formManagerId: 7,
            actionButtons: {
                'vekalet_dosya': [
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.id;
                            if (id) {
                                //yuklemeler/vekalet miniodan getirilmeli
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    }
                ],
                'KEY_VEKALET': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const id = record.id;
                            handleOpenForm(18, 'Vekaletname Yükle', 'vekaletler', 'id', id);
                        }
                    },
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.id;
                            handleOpenForm(18, 'Vekaletname Şube', 'vekaletler', 'id', id);
                        }
                    }
                ]
            }
        },
        {
            key: 'markalar',
            label: 'Markalar',
            sqlKey: 'markalar',
            formManagerId: 10,
            actionButtons: {
                'KEY_MARKA_VIEW': [
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.key_marka;
                            if (id) {
                                //yuklemeler/marka miniodan getirilmeli
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    }
                ],
                'KEY_MARKA': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const key = Object.keys(record);
                            const id = record.key_marka;
                            handleOpenForm(21, 'Tescil Belgesi Yükle', 'markalar', 'id', id);
                        }
                    }
                ]
            }
        },
        {
            key: 'sertifikalar',
            label: 'Sertifikalar',
            sqlKey: 'sertifikalar',
            formManagerId: 9,
            actionButtons: {
                'KEY_SERTIFIKA_VIEW': [
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.id;
                            if (id) {
                                //yuklemeler/sertifika miniodan getirilmeli
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    }
                ],
                'KEY_SERTIFIKA': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const id = record.id;
                            handleOpenForm(22, 'Sertifika Yükle', 'sertifikalar', 'id', id);
                        }
                    }
                ]
            }
        },
        {
            key: 'adresler',
            label: 'Adresler',
            sqlKey: 'adresler',
            formManagerId: 8,
            actionButtons: {
                'KEY_ADRES': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const id = record.key_adres;
                            handleOpenForm(20, 'Adres', 'adresler', 'id', id);
                        }
                    }
                ]
            }
        },
        {
            key: 'ofis-kira-sozlesmeler',
            label: 'Ofis Kira Sözleşmeleri',
            sqlKey: 'ofis-kira-sozlesmeler',
            formManagerId: 70,
            actionButtons: {
                'KEY_OYS': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'PlusOutlined',
                        onClick: (record) => {
                            const id = record.key_oys;
                            handleOpenForm(105, 'Sözleşme Dosyası', 'dosyalar', 'id', id);
                        }
                    },
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const id = record.key_oys;
                            handleOpenForm(227, 'Kiralık Yer Sözleşme Bilgileri', 'ofis_ys_sozlesme', 'id', id);
                        }
                    },
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.key_oys;
                            handleOpenDetailModal(record, null, 'main-table');
                        }
                    }
                ]
            }
        },
        {
            key: 'ofis-sigorta-police',
            label: 'Sigorta Poliçeleri',
            sqlKey: 'ofis-sigorta-police',
            formManagerId: 147,
            actionButtons: {
                'KEY_OFISSIGORTA': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'PlusOutlined',
                        onClick: (record) => {
                            const id = record.key_ofissigorta;
                            handleOpenForm(106, 'Firma Sigorta Poliçe Dosyası', 'dosyalar', 'id', id);
                        }
                    },
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => {
                            const id = record.key_ofissigorta;
                            handleOpenForm(228, 'Firma Sigorta Poliçeleri', 'ofis_sigorta_police', 'id', id);
                        }
                    },
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.key_ofissigorta;
                            handleOpenDetailModal(record, null, 'main-table');
                        }
                    }
                ]
            }
        },
    ];

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const activeTabData = sirketlerTabs.find(tab => tab.key === activeTab);
            if (!activeTabData) {
                console.warn('Active tab data not found for key:', activeTab);
                return;
            }
            const url = `${API_BASE_URL}/list/execute/${activeTabData.sqlKey}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();
            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }

            let filteredData = [...allData];
            
            const currentCompanyFilter = companyFilters[activeTab] || null;
            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }
            
            if (currentCompanyFilter && currentCompanyFilter > 0) {                
                const selectedCompany = companies.find(company => 
                    (company.id || company.key_firma) == currentCompanyFilter
                );
                const selectedCompanyName = selectedCompany ? 
                    (selectedCompany.firma_adi || selectedCompany.Ad) : null;
                
                filteredData = filteredData.filter(record => {
                    const companyName = record.firma2 || record.firma_adi || record.firma || record.sirket_adi;
                    const matches = companyName === selectedCompanyName;
                    return matches;
                });
            }

            if (filteredData && filteredData.length > 0) {
                const sample = filteredData[0];
                const keys = Object.keys(sample).filter(key => !String(key).toLowerCase().includes('_row_style'));
                const dynamicCols = keys.map((key, index) => {
                    const firstValue = sample[key];
                    const dateCol = isDateField(key);
                    const valuesForKey = filteredData
                        .map((item) => item[key])
                        .filter((val) => val !== undefined && val !== null && val !== '');
                    const numericRegexes = [/^₺?[\d.,]+$/, /^\$?[\d.,]+$/, /^€?[\d.,]+$/, /^[\d.,]+$/];
                    const allNumericLike = valuesForKey.length > 0 && valuesForKey.every((val) => {
                        if (typeof val === 'number' && !isNaN(val)) return true;
                        if (typeof val === 'string') {
                            if (/[A-Za-z\-]/.test(val)) return false;
                            return numericRegexes.some((re) => re.test(val));
                        }
                        return false;
                    });
                    const numberCol = !dateCol && allNumericLike && (
                        key.toLowerCase().includes('tutar') ||
                        key.toLowerCase().includes('fiyat') ||
                        key.toLowerCase().includes('ücret') ||
                        key.toLowerCase().includes('miktar') ||
                        key.toLowerCase().includes('adet') ||
                        key.toLowerCase().includes('kesilen') ||
                        key.toLowerCase().includes('toplam') ||
                        allNumericLike
                    );
                    const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                        (val) => val !== undefined && val !== null && val !== ''
                    );
                    let filters = [];
                    if (numberCol) filters = getNumericFilters();
                    else if (dateCol) filters = getDateFilters();
                    else filters = getStringFilters();
                    return {
                        key,
                        title: key,
                        dataIndex: key,
                        isDateColumn: dateCol,
                        isNumber: numberCol,
                        isString: !numberCol && !dateCol,
                        uniqueValues,
                        sortable: true,
                        index,
                    };
                });
                setColumns(dynamicCols);
            } else {
                setColumns([]);
            }

            setData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));

        } catch (err) {
            console.error('Şirketler data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters.search, companyFilters]);

    const handlePivotRedirect = useCallback(() => {
        const activeTabData = sirketlerTabs.find(tab => tab.key === activeTab);
        if (!activeTabData?.sqlKey) return;
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: activeTabData.label || 'Şirketler',
            sqlKey: activeTabData.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [activeTab, data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;
        let newOrder = 'asc';
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === 'asc') newOrder = 'desc';
            else if (sortedInfo.order === 'desc') newOrder = null;
        }
        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search || '')
            );
            const matchesFilters = columns.length > 0 ? Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            }) : true;
            return matchesSearch && matchesFilters;
        });
        if (sortedInfo.columnKey && sortedInfo.order && columns.length > 0) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];
                    if (column.isDateColumn) {
                        const dateA = parseDate(valA);
                        const dateB = parseDate(valB);
                        if (!dateA && !dateB) return 0;
                        if (!dateA) return sortedInfo.order === 'asc' ? -1 : 1;
                        if (!dateB) return sortedInfo.order === 'asc' ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === 'asc' ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = parseValue(valA);
                        const numB = parseValue(valB);
                        return sortedInfo.order === 'asc' ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || '').toLowerCase();
                        const strB = String(valB || '').toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === 'asc' ? comparison : -comparison;
                    }
                });
            }
        }
        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters(prev => ({
            ...prev,
            search: ''
        }));
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const getSortIcon = (columnKey) => {
        if (sortedInfo.columnKey === columnKey) {
            return sortedInfo.order === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />;
        }
        return null;
    };

    const saveSelectedColumnsToStorage = useCallback((columnKeys) => {
        try {
            const storageKey = `sirketler_selected_columns_${activeTab}`;
            localStorage.setItem(storageKey, JSON.stringify(columnKeys));
        } catch (error) {
            console.error('Error saving selected columns to storage:', error);
        }
    }, [activeTab]);

    useEffect(() => {
        if (columns.length > 0) {
            try {
                const storageKey = `sirketler_selected_columns_${activeTab}`;
                const saved = localStorage.getItem(storageKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const validColumns = parsed.filter(key => columns.some(col => col.key === key));
                    if (validColumns.length > 0) {
                        setSelectedColumns(validColumns);
                    } else {
                        const allColumnKeys = columns.map(col => col.key);
                        setSelectedColumns(allColumnKeys);
                        saveSelectedColumnsToStorage(allColumnKeys);
                    }
                } else {
                    const allColumnKeys = columns.map(col => col.key);
                    setSelectedColumns(allColumnKeys);
                    saveSelectedColumnsToStorage(allColumnKeys);
                }
            } catch (error) {
                console.error('Error loading selected columns from storage:', error);
                const allColumnKeys = columns.map(col => col.key);
                setSelectedColumns(allColumnKeys);
            }
        }
    }, [columns, saveSelectedColumnsToStorage, activeTab]);

    const handleColumnSelectionChange = (checkedValues) => {
        setSelectedColumns(checkedValues);
        saveSelectedColumnsToStorage(checkedValues);
    };

    const handleSelectAllColumns = (checked) => {
        if (checked) {
            const allColumnKeys = columns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys);
        } else {
            setSelectedColumns([]);
            saveSelectedColumnsToStorage([]);
        }
    };

    const visibleColumns = useMemo(() => {
        if (selectedColumns.length === 0) {
            return columns;
        }
        return columns.filter(col => selectedColumns.includes(col.key));
    }, [columns, selectedColumns]);

    const removeColumn = (columnKey) => {
        setSelectedColumns((prev) => {
            const newSelected = prev.filter((key) => key !== columnKey);
            saveSelectedColumnsToStorage(newSelected);
            return newSelected;
        });
    };

    const getFilterDropdown = (column, darkMode, handleAdvancedFilterChange, clearColumnFilter, filteredInfo) => {
        if (!column) return null;
        const currentFilter = filteredInfo[column.key] || {};
        const condition = currentFilter.condition || 'choose';
        const isString = column.isString;
        const isNumber = column.isNumber;
        const isDateColumn = column.isDateColumn;
        const filtersList = isNumber ? getNumericFilters() : isDateColumn ? getDateFilters() : getStringFilters();
        return (
            <div style={{ padding: 16, minWidth: 300 }}>
                <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : 'var(--color-bg)', border: `1px solid ${darkMode ? 'var(--dark-border)' : 'var(--color-theme)'}`, padding: 12}}>
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)' }}>
                            Filtre Şartı
                        </Text>
                        <Select
                            size="small"
                            value={condition}
                            onChange={(value) => handleAdvancedFilterChange(column.key, 'condition', value)}
                            style={{ width: '100%' }}
                            className={darkMode ? 'dark-select' : ''}
                        >
                            {filtersList.map((filter) => (
                                <Option key={filter.value} value={filter.value}>
                                    {filter.text}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {isString && condition === 'select' && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Değer Seç
                            </Text>
                            <Select
                                size="small"
                                value={currentFilter.value || ''}
                                onChange={(value) => handleAdvancedFilterChange(column.key, 'value', value)}
                                style={{ width: '100%' }}
                                placeholder="Seçiniz"
                                className={darkMode ? 'dark-select' : ''}
                            >
                                <Option value="">Seçiniz</Option>
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {isNumber && condition !== 'choose' && condition !== 'between' && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ''}
                                onChange={(value) => handleAdvancedFilterChange(column.key, 'value', value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    )}

                    {isNumber && condition === 'between' && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Min Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Min"
                                    value={currentFilter.min || ''}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, 'min', value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Max Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Max"
                                    value={currentFilter.max || ''}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, 'max', value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                        </>
                    )}

                    {isString && !['choose', 'empty', 'notEmpty', 'select'].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Değer
                            </Text>
                            <Input
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ''}
                                onChange={(e) => handleAdvancedFilterChange(column.key, 'value', e.target.value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    )}

                    {column.isDateColumn && ['equals', 'before', 'after'].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Tarih
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.value ? dayjs(currentFilter.value) : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, 'value', date ? date.format('YYYY-MM-DD') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        </div>
                    )}

                    {column.isDateColumn && condition === 'between' && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Başlangıç Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.startDate ? dayjs(currentFilter.startDate) : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, 'startDate', date ? date.format('YYYY-MM-DD') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Bitiş Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.endDate ? dayjs(currentFilter.endDate) : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, 'endDate', date ? date.format('YYYY-MM-DD') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                        </>
                    )}

                    <Button
                        size="small"
                        onClick={() => clearColumnFilter(column.key)}
                        style={{ marginTop: 8, width: '100%', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}
                        className={darkMode ? 'dark-button' : ''}
                    >
                        Filtreyi Sıfırla
                    </Button>
                </Space>
            </div>
        );
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        const newTabData = sirketlerTabs.find(tab => tab.key === key);
        setFilters({
            search: '',
            durum: null,
            firma: null,
            page: 1,
            pageSize: 25,
        });
        setPagination({
            current: 1,
            total: 0,
            pageSize: 10
        });
        const tabCompanyFilter = companyFilters[key];
        setSelectedCompanyId(tabCompanyFilter || null);
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const loadCompanies = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/list/execute/sirketler`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const companiesData = await response.json();
            if (Array.isArray(companiesData)) {
                setCompanies(companiesData);
            }
        } catch (err) {
            console.error('Companies load error:', err);
        }
    }, []);

    const handleCompanySelect = (companyId) => {
        setCompanyFilters(prev => ({
            ...prev,
            [activeTab]: companyId
        }));
        setSelectedCompanyId(companyId);
    };

    const handleOpenDetailModal = async (record, firstChar = null, modalType = 'main-table') => {
        const id = record.key_demirbas_zimmet || record.id || record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }

        const idString = String(id);
        setCurrentRecordId(idString);
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setModalButtonType(modalType);

        try {
            const charToUse = firstChar || idString.charAt(0).toLowerCase();
            let queryName = 'demirbas';
            let modalTitle = 'Şirket Detayları';

            if (modalType === 'sub-table') {
                queryName = 'demirbas';
                modalTitle = 'Şirket Detayları';
            } else {
                switch (charToUse) {
                    case 'k':
                        modalTitle = 'Kredi Kartı Zimmet Detayları';
                        queryName = 'zimmet-detail-k';
                        break;
                    case 'a':
                        modalTitle = 'Araç Zimmet Detayları';
                        queryName = 'zimmet-detail-a';
                        break;
                    case 'd':
                        modalTitle = 'Demirbaş Zimmet Detayları';
                        queryName = 'zimmet-detail-d';
                        break;
                    default:
                        queryName = 'demirbas';
                        modalTitle = 'Demi  rbaş Detayları';
                        break;
                }
            }

            let paramId = idString;
            if (modalType === 'sub-table' && queryName === 'demirbas') {
                paramId = record.demirbas_id || record.id || idString;
            }

            const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: { id: paramId } })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDetailData(Array.isArray(data) ? data : []);

            setModalTitle(modalTitle);
        } catch (err) {
            console.error('Detail modal data load error:', err);
            setDetailError('Detay verileri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setDetailModalOpen(false);
        setDetailData([]);
        setDetailError(null);
        setCurrentRecordId(null);
        setModalButtonType('main-table');
    };

    const handleViewFile = (record) => {
        const filePath = record.file;
        if (filePath) {
            const fileUrl = `${API_BASE_URL}/files/${filePath}`;
            window.open(fileUrl, '_blank');
        } else {
            message.warning('Dosya bulunamadı');
        }
    };

    const handleEditFile = (record) => {
        const id = record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(98, 'Demirbaş Dosya Düzenle', 'zimmet', 'id', id);
    };

    const handleSubEditFile = (record) => {
        const id = record.dosyaID;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(103, 'Demirbaş Dosya Düzenle', 'demirbas_dosya', 'id', id);
    };

    const handleAddDelivery = (record) => {
        const id = record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(100, 'Zimmet Teslim Formu', 'zimmet', 'id', id);
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    return (
        <div className={`fatura-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`fatura-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Şirketler
                                <EditOutlined onClick={() => {
                                    const activeTabData = sirketlerTabs.find(tab => tab.key === activeTab);
                                    const formManagerId = activeTabData?.formManagerId;
                                    handleOpenForm(275, 'Liste Formu', 'sqls', 'id', formManagerId);
                                }} />
                            </Text>
                            <Select
                                placeholder="Şirket Türü Seçin"
                                value={activeTab}
                                onChange={(value) => handleTabChange(value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {sirketlerTabs.map(tab => (
                                    <Option key={tab.key} value={tab.key}>
                                        {tab.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>

                    {/* Şirket Seçimi */}
                    <Col xs={24} sm={12} md={4} lg={3}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Şirket Listesi
                            </Text>
                            <Select
                                placeholder="Şirket seçin"
                                value={companyFilters[activeTab] || selectedCompanyId}
                                onChange={handleCompanySelect}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                                showSearch
                                filterOption={(input, option) =>
                                    trIncludes(option?.children ?? '', input)
                                }
                                loading={loading}
                            >
                                <Option key={-2} value={-2}>Tümü</Option>
                                <Option key={-1} value={-1}>Aktif Şirketler</Option>
                                {companies.map(company => {
                                    const companyName = company.firma_adi || company.Ad;
                                    return (
                                        <Option key={company.id || company.key_firma} value={company.id || company.key_firma}>
                                            {companyName}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </div>
                    </Col>

                    {/* Arama */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları */}
                    <Col xs={24} sm={24} md={6} lg={12}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    icon={<ClearOutlined />}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: sirketlerTabs.find(t => t.key === activeTab)?.label || 'Şirketler', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    open={columnSelectDropdownOpen}
                                    onOpenChange={setColumnSelectDropdownOpen}
                                    trigger={['click']}
                                    dropdownRender={() => (
                                        <div style={{
                                            padding: '2px',
                                            minWidth: '250px',
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                                                <Checkbox
                                                    checked={selectedColumns.length === columns.length && columns.length > 0}
                                                    indeterminate={selectedColumns.length > 0 && selectedColumns.length < columns.length}
                                                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                                    style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                                                >
                                                    <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                        Tümünü Seç
                                                    </Text>
                                                </Checkbox>
                                            </div>
                                            <Checkbox.Group
                                                value={selectedColumns}
                                                onChange={handleColumnSelectionChange}
                                                style={{ width: '100%' }}
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {columns.map(column => (
                                                        <Checkbox
                                                            key={column.key}
                                                            value={column.key}
                                                            style={{
                                                                width: '100%',
                                                                color: darkMode ? 'var(--dark-text)' : undefined
                                                            }}
                                                        >
                                                            {column.title}
                                                        </Checkbox>
                                                    ))}
                                                </Space>
                                            </Checkbox.Group>
                                        </div>
                                    )}
                                >
                                    <Button
                                        icon={<EyeOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Sütun Seç
                                    </Button>
                                </Dropdown>
                                {/* Chatbot */}
                                <Chatbot />
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tablo */}
            <Card
                className={`fatura-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="fatura-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={handlePivotRedirect}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>

                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={filteredAndSortedData}
                        loading={loading}
                        rowKey={(record) => record.id || record.key_firma || record.No || Math.random()}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        rowClassName={(record) => {
                            return isExpired(record) ? 'expired-row' : '';
                        }}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} kayıt`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={visibleColumns.map((column, index) => {
                            const activeTabData = sirketlerTabs.find(tab => tab.key === activeTab);
                            const hasActionButtons = activeTabData && activeTabData.actionButtons;
                            const key = column.key;
                            const columnKey = column.key || '';
                            const normalizedKey = columnKey.replace(/\s+/g, '_').toUpperCase();
                            const actionButtonsToShow = hasActionButtons ? (
                                activeTabData.actionButtons[columnKey] || 
                                activeTabData.actionButtons[normalizedKey] || 
                                activeTabData.actionButtons[columnKey.toLowerCase()] ||
                                activeTabData.actionButtons[columnKey.toUpperCase()] ||
                                []
                            ) : [];
                            const shouldShowActionButtons = actionButtonsToShow.length > 0;

                            return {
                                ...column,
                                filters: undefined,
                                filterDropdown: false,
                                title: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ flex: 1, cursor: 'pointer' }}>{column.title}</span>
                                        <Dropdown
                                            dropdownRender={() => getFilterDropdown(column, darkMode, handleAdvancedFilterChange, clearColumnFilter, filteredInfo)}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                style={{ marginLeft: 4, color: filteredInfo[key] ? (darkMode ? 'var(--dark-text)' : 'var(--color-theme)') : '#ccc' }}
                                                className={darkMode ? 'dark-button' : ''}
                                            >
                                                <FilterOutlined style={{ fontSize: 12 }} />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ),
                                onHeaderCell: () => ({
                                    onContextMenu: (e) => {
                                        e.preventDefault();
                                        removeColumn(column.key);
                                    },
                                    onClick: () => {
                                        if (column.sortable) {
                                            handleSort(column.key);
                                        }
                                    }
                                }),
                                dataIndex: key,
                                key: key,
                                width: 50,
                                align: 'center',
                                ellipsis: { showTitle: true },
                                showSorterTooltip: false,
                                render: (text, record) => (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {shouldShowActionButtons ? (
                                            <ActionButtons
                                                record={record}
                                                actionButtons={actionButtonsToShow}
                                                darkMode={darkMode}
                                            />
                                        ) : (
                                            renderCellContent(key, text)
                                        )}
                                    </div>
                                ),
                            };
                        })}
                    />
                </div>
            </Card>

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

            {/* Detail Modal */}
            {detailModalOpen && (
                <Modal
                    title={modalTitle}
                    open={detailModalOpen}
                    onCancel={closeDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={1200}
                    style={{ top: 20 }}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        {detailError && (
                            <Alert
                                message="Hata"
                                description={detailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Table
                            dataSource={detailData}
                            loading={detailLoading}
                            rowKey={(record) => record.dosyaID || record.id || Math.random()}
                            scroll={{ x: 'max-content' }}
                            className={darkMode ? 'dark-table' : ''}
                            rowClassName={(record) => {
                                return isExpired(record) ? 'expired-row' : '';
                            }}
                            size="small"
                            style={{
                                width: '100%',
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                            }}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} / ${total} kayıt`,
                                pageSizeOptions: ['10', '25', '50', '100']
                            }}
                        >
                            {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                                const totalColumns = Object.keys(detailData[0]).length;
                                const isLastColumn = index === totalColumns - 1;

                                return (
                                    <Table.Column
                                        key={key}
                                        title={key}
                                        dataIndex={key}
                                        width={50}
                                        ellipsis={{
                                            showTitle: true
                                        }}
                                        render={(text, record) => (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {isLastColumn ? (
                                                    <Space size="small">
                                                        {modalButtonType === 'main-table' ? (
                                                            <>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        handleEditFile(record);
                                                                    }}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Düzenle
                                                                </Button>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<PlusOutlined />}
                                                                    onClick={() => handleAddDelivery(record)}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Teslimat Ekle
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EyeOutlined />}
                                                                    onClick={() => handleViewFile(record)}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Görüntüle
                                                                </Button>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        handleSubEditFile(record);
                                                                    }}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Düzenle
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Space>
                                                ) : (
                                                    renderCellContent(key, text)
                                                )}
                                            </div>
                                        )}
                                        sorter={(a, b) => {
                                            const aVal = a[key];
                                            const bVal = b[key];
                                            if (!isNaN(aVal) && !isNaN(bVal)) {
                                                return Number(aVal) - Number(bVal);
                                            }
                                            return String(aVal).localeCompare(String(bVal), 'tr');
                                        }}
                                    />
                                );
                            })}
                        </Table>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SirketlerPage;